import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField, Button, Container, Typography, Box, Grid, Paper,
  Avatar
} from '@mui/material';
import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';
import CategoryIcon from '@mui/icons-material/Category';
import Swal from 'sweetalert2';
import { message } from 'antd';

const validationSchema = Yup.object({
  serviceType: Yup.string()
    .required('Nombre del tipo de servicio es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(200, 'El nombre no puede exceder 200 caracteres'),
});

const formatValues = (values) => {
  const serviceType = {
    serviceType: values.serviceType.trim(),
  };
  return serviceType;
};

function AddServiceType() {
  const [existingServiceTypes, setExistingServiceTypes] = useState([]);
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const token = localStorage.getItem('token');

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getExistingServiceTypes = async () => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceTypes-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
        setExistingServiceTypes([]);
      } else {
        setExistingServiceTypes(data);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar tipos de servicio');
      setExistingServiceTypes([]);
    }
  };

  const postServiceType = async (serviceType) => {
    Swal.fire({
      title: 'Creando artículo...',
      text: 'Por favor espera mientras se crea el artículo.',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceType-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(serviceType)
      });
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
        Swal.close();
        Swal.fire({
          title: 'Error al registrar tipo de servicio',
          text: 'Ha ocurrido un error al registrar el tipo de servicio, por favor intenta de nuevo.',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#f44336'
        });
        return false;
      } else {
        Swal.close();
        Swal.fire({
          title: 'Tipo de servicio registrado',
          text: 'El tipo de servicio ha sido registrado exitosamente.',
          icon: 'success',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#4caf50'
        });
        return true;
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: 'Ha ocurrido un error inesperado.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f44336'
      });
      return false;
    }
  };

  const formik = useFormik({
    initialValues: {
      serviceType: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {

      // Check for duplicate service type
      const isDuplicate = existingServiceTypes.some(
        type => type.serviceType.toLowerCase() === values.serviceType.trim().toLowerCase()
      );

      if (isDuplicate) {
        Swal.fire({
          title: 'Error',
          text: 'El tipo de servicio ya existe. Por favor, ingresa un tipo de servicio diferente.',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#f44336'
        });
        return;
      }

      const serviceType = formatValues(values);

      const confirmReq = await Swal.fire({
        title: 'Confirmar creación de tipo de servicio',
        text: '¿Estás seguro/a de que quieres registrar este tipo de servicio?',
        icon: 'question',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Registrar tipo de servicio',
        confirmButtonColor: '#4caf50'
      });

      if (confirmReq.isConfirmed) {
        const isPosted = await postServiceType(serviceType);
        if (isPosted) {
          formik.resetForm();
          // Refresh the existing service types list
          getExistingServiceTypes();
        }
      }
    },
  });

  useEffect(() => {
    if (!permissionError && !isLoading) {
      getExistingServiceTypes();
    }
  }, [permissionError, isLoading]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      {contextHolder}
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <Avatar sx={{ bgcolor: '#f759ab', mr: 2 }}>
            <CategoryIcon />
          </Avatar>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Registrar tipo de servicio
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 3 }}>
          Añade un nuevo tipo de servicio para clasificar los servicios de tu negocio
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                id="serviceType"
                name="serviceType"
                label="Nombre del tipo de servicio"
                placeholder="Ej: Fotografía, Decoración, Catering..."
                value={formik.values.serviceType}
                onChange={formik.handleChange}
                error={formik.touched.serviceType && Boolean(formik.errors.serviceType)}
                helperText={formik.touched.serviceType && formik.errors.serviceType}
                margin="normal"
                inputProps={{ maxLength: 200 }}
                sx={{ mb: 3 }}
              />
            </Grid>
          </Grid>

          {/* Mostrar tipos de servicios existentes */}
          {existingServiceTypes.length > 0 && (
            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#666' }}>
                Tipos de servicios existentes:
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                maxHeight: '120px',
                overflowY: 'auto',
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1
              }}>
                {existingServiceTypes.map((type, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{
                      bgcolor: '#e3f2fd',
                      px: 2,
                      py: 0.5,
                      borderRadius: 4,
                      border: '1px solid #bbdefb'
                    }}
                  >
                    {type.serviceType}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={!formik.values.serviceType.trim()}
            sx={{
              mt: 3,
              backgroundColor: '#f759ab',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#eb2f96'
              },
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#666'
              },
              padding: '10px 20px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '8px'
            }}
          >
            Registrar tipo de servicio
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default AddServiceType;
